// drag_sort — sort each item into the correct category bucket. On touch, a true
// drag is fiddly for small hands, so we use tap-to-select + tap-bucket (the same
// interaction TapMatch uses). Auto-submits once every item is placed. The
// stripped payload carries no answer key — the server scores placements.

import { useState, type ReactElement } from 'react';

import type { content } from '@fajrarabi/zod-schemas';

import { ActivityImage, ArabicText } from './primitives';
import type { ActivityRendererProps } from './types';

type StrippedItem = { id: string; labelKey: string; image?: string | null };
type Category = { id: string; labelKey: string };
type StrippedSort = {
  instructionKey?: string | null;
  categories: Category[];
  items: StrippedItem[];
};

export function DragSort({ data, ctx, cb }: ActivityRendererProps<StrippedSort>): ReactElement {
  const { t } = ctx;
  const [selected, setSelected] = useState<string | null>(null);
  // itemId → categoryId
  const [placed, setPlaced] = useState<Record<string, string>>({});

  const unplaced = data.items.filter((i) => !(i.id in placed));

  function place(categoryId: string): void {
    if (cb.locked || !selected) return;
    setPlaced((cur) => {
      const next = { ...cur, [selected]: categoryId };
      if (Object.keys(next).length === data.items.length) {
        cb.onAnswer({
          kind: 'sort',
          placements: Object.entries(next).map(([itemId, catId]) => ({
            itemId,
            categoryId: catId,
          })),
        } satisfies content.AttemptInput);
      }
      return next;
    });
    setSelected(null);
  }

  function unplace(itemId: string): void {
    if (cb.locked) return;
    setPlaced((cur) => {
      const next = { ...cur };
      delete next[itemId];
      return next;
    });
  }

  return (
    <div className="mx-auto flex w-full max-w-md flex-col">
      <p className="text-center text-lg font-semibold text-ink-700">
        {t(data.instructionKey ?? 'content.drag_sort.instruction')}
      </p>

      {/* Item tray — the pool of unplaced items. */}
      <div className="mt-5 flex min-h-[64px] flex-wrap justify-center gap-2 rounded-2xl bg-cream-100 p-3">
        {unplaced.length === 0 ? (
          <span className="self-center text-sm text-ink-500">
            {t('content.drag_sort.all_sorted')}
          </span>
        ) : (
          unplaced.map((item) => (
            <button
              key={item.id}
              type="button"
              disabled={cb.locked}
              onClick={() => setSelected((cur) => (cur === item.id ? null : item.id))}
              aria-pressed={selected === item.id}
              className={`inline-flex min-h-[48px] items-center gap-2 rounded-full border-2 px-4 text-lg font-bold transition-colors ${
                selected === item.id
                  ? 'border-burgundy-500 bg-burgundy-50 text-burgundy-700'
                  : 'border-cream-200 bg-white text-ink-900 hover:border-sun-300'
              }`}
            >
              {item.image ? (
                <span className="h-8 w-8 shrink-0">
                  <ActivityImage
                    src={item.image}
                    ctx={ctx}
                    alt={t(item.labelKey)}
                    fallbackEmoji="❓"
                  />
                </span>
              ) : null}
              <ArabicText text={t(item.labelKey)} ctx={ctx} />
            </button>
          ))
        )}
      </div>

      {/* Category buckets. */}
      <div
        className="mt-5 grid gap-3"
        style={{ gridTemplateColumns: `repeat(${data.categories.length}, minmax(0, 1fr))` }}
      >
        {data.categories.map((cat) => {
          const contents = data.items.filter((i) => placed[i.id] === cat.id);
          return (
            <button
              key={cat.id}
              type="button"
              disabled={cb.locked || !selected}
              onClick={() => place(cat.id)}
              className="flex min-h-[120px] flex-col rounded-2xl border-2 border-dashed border-cream-200 bg-white p-2 text-start transition-colors enabled:hover:border-sun-300 disabled:opacity-90"
            >
              <span className="mb-2 text-center text-sm font-bold text-ink-500">
                <ArabicText text={t(cat.labelKey)} ctx={ctx} />
              </span>
              <span className="flex flex-1 flex-wrap content-start justify-center gap-1">
                {contents.map((i) => (
                  <span
                    key={i.id}
                    role="button"
                    tabIndex={cb.locked ? -1 : 0}
                    onClick={(e) => {
                      e.stopPropagation();
                      unplace(i.id);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.stopPropagation();
                        unplace(i.id);
                      }
                    }}
                    className="inline-flex items-center rounded-full bg-leaf/15 px-3 py-1 text-sm font-bold text-burgundy-700"
                  >
                    <ArabicText text={t(i.labelKey)} ctx={ctx} />
                  </span>
                ))}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
